export const FORM_FIELD_TYPES = Object.freeze([
  {
    value: "fullName",
    name: "name",
    inputType: "text",
    labelKey: "fullName",
    fallbackLabel: "Name",
  },
  {
    value: "firstName",
    name: "first_name",
    inputType: "text",
    labelKey: "firstName",
    fallbackLabel: "First Name",
  },
  {
    value: "lastName",
    name: "last_name",
    inputType: "text",
    labelKey: "lastName",
    fallbackLabel: "Last Name",
  },
  {
    value: "email",
    name: "email",
    inputType: "email",
    labelKey: "email",
    fallbackLabel: "Email",
  },
  {
    value: "phone",
    name: "phone",
    inputType: "tel",
    labelKey: "phone",
    fallbackLabel: "Phone",
  },
  {
    value: "vehicle",
    name: "vehicle",
    inputType: "text",
    labelKey: "vehicle",
    fallbackLabel: "Vehicle",
  },
  {
    value: "vehicleMake",
    name: "vehicle_make",
    inputType: "text",
    labelKey: "vehicleMake",
    fallbackLabel: "Vehicle make",
  },
  {
    value: "vehicleModel",
    name: "vehicle_model",
    inputType: "text",
    labelKey: "vehicleModel",
    fallbackLabel: "Vehicle model",
  },
  {
    value: "vehicleYear",
    name: "vehicle_year",
    inputType: "number",
    labelKey: "vehicleYear",
    fallbackLabel: "Vehicle year",
  },
  {
    value: "mileage",
    name: "mileage",
    inputType: "number",
    labelKey: "mileage",
    fallbackLabel: "Vehicle mileage",
  },
  {
    value: "vin",
    name: "vin",
    inputType: "text",
    labelKey: "vin",
    fallbackLabel: "VIN",
  },
  {
    value: "text",
    name: "text",
    inputType: "text",
    labelKey: "shortText",
    fallbackLabel: "Short text",
  },
  {
    value: "number",
    name: "number",
    inputType: "number",
    labelKey: "number",
    fallbackLabel: "Number",
  },
  {
    value: "date",
    name: "date",
    inputType: "date",
    labelKey: "date",
    fallbackLabel: "Date",
  },
  {
    value: "message",
    name: "message",
    inputType: "textarea",
    labelKey: "message",
    fallbackLabel: "Message",
  },
  {
    value: "textarea",
    name: "long_text",
    inputType: "textarea",
    labelKey: "textarea",
    fallbackLabel: "Long text",
  },
  {
    value: "select",
    name: "selection",
    inputType: "select",
    labelKey: "select",
    fallbackLabel: "Select",
    hasOptions: true,
  },
  {
    value: "radio",
    name: "choice",
    inputType: "radio",
    labelKey: "radio",
    fallbackLabel: "Radio group",
    hasOptions: true,
  },
  {
    value: "checkbox",
    name: "consent",
    inputType: "checkbox",
    labelKey: "checkbox",
    fallbackLabel: "Checkbox",
  },
]);

const FIELD_TYPE_BY_VALUE = new Map(
  FORM_FIELD_TYPES.map((definition) => [definition.value, definition])
);

const cleanIdentifier = (value, fallback) => {
  const cleaned = String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return cleaned || fallback;
};

const createUniqueIdentifier = (requested, used, fallback) => {
  const base = cleanIdentifier(requested, fallback);
  let candidate = base;
  let suffix = 2;
  while (used.has(candidate)) {
    candidate = `${base}_${suffix}`;
    suffix += 1;
  }
  used.add(candidate);
  return candidate;
};

const matchesName = (name, values) => values.some((value) => name === value || name.includes(value));

export const getFormFieldTypeDefinition = (fieldType) =>
  FIELD_TYPE_BY_VALUE.get(fieldType) || FIELD_TYPE_BY_VALUE.get("text");

/**
 * Infer the semantic field type for legacy fields that only stored HTML type/name.
 */
export const resolveFormFieldType = (field = {}) => {
  if (FIELD_TYPE_BY_VALUE.has(field.fieldType)) return field.fieldType;

  const name = String(field.name || field.id || "").trim().toLowerCase();
  const htmlType = String(field.type || "text").trim().toLowerCase();

  if (matchesName(name, ["mileage", "odometer", "kilometer", "kilometre", "vehicle_km", "car_km"]) || name === "km") {
    return "mileage";
  }
  if (matchesName(name, ["vehicle_year", "model_year", "car_year"])) return "vehicleYear";
  if (matchesName(name, ["vehicle_make", "car_make"]) || name === "make") return "vehicleMake";
  if (matchesName(name, ["vehicle_model", "car_model"]) || name === "model") return "vehicleModel";
  if (matchesName(name, ["vin", "chassis"])) return "vin";
  if (matchesName(name, ["vehicle", "car"])) return "vehicle";
  if (matchesName(name, ["email", "e_mail"]) || htmlType === "email") return "email";
  if (matchesName(name, ["phone", "telephone", "mobile", "tel"]) || htmlType === "tel") return "phone";
  if (matchesName(name, ["long_text", "longtext", "textarea"])) return "textarea";
  if (matchesName(name, ["message", "comment", "notes", "description"]) || htmlType === "textarea") {
    return "message";
  }
  if (matchesName(name, ["first_name", "firstname", "given_name"])) return "firstName";
  if (matchesName(name, ["last_name", "lastname", "family_name", "surname"])) return "lastName";
  if (matchesName(name, ["full_name", "fullname"]) || name === "name") return "fullName";
  if (htmlType === "number") return "number";
  if (htmlType === "date") return "date";
  return "text";
};

/**
 * Normalize legacy/current fields into a stable, safe and uniquely named list.
 */
export const normalizeFormFields = (fields) => {
  if (!Array.isArray(fields)) return [];

  const usedIds = new Set();
  const usedNames = new Set();

  return fields
    .filter((field) => field && typeof field === "object")
    .map((field, index) => {
      const fieldType = resolveFormFieldType(field);
      const definition = getFormFieldTypeDefinition(fieldType);
      const fallbackId = `field_${index + 1}`;
      const id = createUniqueIdentifier(field.id || field.name, usedIds, fallbackId);
      const name = createUniqueIdentifier(field.name || definition.name, usedNames, definition.name);

      return {
        ...field,
        id,
        fieldType,
        name,
        type: definition.inputType,
        label:
          typeof field.label === "string" && field.label.trim()
            ? field.label
            : definition.fallbackLabel,
        placeholder: typeof field.placeholder === "string" ? field.placeholder : "",
        required: Boolean(field.required),
        ...(definition.hasOptions
          ? {
              options: normalizeFormFieldOptions(
                field.options?.length
                  ? field.options
                  : [
                      { label: "Option 1", value: "option_1" },
                      { label: "Option 2", value: "option_2" },
                    ]
              ),
            }
          : {}),
        ...(normalizeConstraint(field.minLength, { min: 0, max: 4000 }) !== undefined
          ? { minLength: normalizeConstraint(field.minLength, { min: 0, max: 4000 }) }
          : {}),
        ...(normalizeConstraint(field.maxLength, { min: 1, max: 4000 }) !== undefined
          ? { maxLength: normalizeConstraint(field.maxLength, { min: 1, max: 4000 }) }
          : {}),
        ...(normalizeConstraint(field.min) !== undefined
          ? { min: normalizeConstraint(field.min) }
          : {}),
        ...(normalizeConstraint(field.max) !== undefined
          ? { max: normalizeConstraint(field.max) }
          : {}),
        ...(field.pattern ? { pattern: String(field.pattern).slice(0, 300) } : {}),
        ...(field.requiredMessage
          ? { requiredMessage: String(field.requiredMessage).slice(0, 500) }
          : {}),
        ...(field.validationMessage
          ? { validationMessage: String(field.validationMessage).slice(0, 500) }
          : {}),
      };
    });
};

export const normalizeFormFieldOptions = (options) => {
  if (!Array.isArray(options)) return [];
  const usedValues = new Set();
  const usedIds = new Set();
  return options
    .filter(
      (option) =>
        option !== null &&
        option !== undefined &&
        (["string", "number"].includes(typeof option) || typeof option === "object")
    )
    .map((source, index) => {
      const option =
        typeof source === "object"
          ? source
          : { label: String(source), value: String(source) };
      const value = createUniqueIdentifier(
        option.value || option.label,
        usedValues,
        `option_${index + 1}`
      );
      return {
        id: createUniqueIdentifier(
          option.id || value,
          usedIds,
          `option_${index + 1}`
        ),
        label: String(option.label || option.value || `Option ${index + 1}`)
          .trim()
          .slice(0, 160),
        value,
      };
    });
};

const normalizeConstraint = (value, { min = -Infinity, max = Infinity } = {}) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return Math.min(max, Math.max(min, numeric));
};

const createGeneratedFieldId = () =>
  `form-field-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const createFormField = ({
  fieldType = "text",
  fields = [],
  id,
  label,
  placeholder = "",
  required = false,
} = {}) => {
  const definition = getFormFieldTypeDefinition(fieldType);
  const normalized = normalizeFormFields(fields);
  const usedNames = new Set(normalized.map((field) => field.name));
  const usedIds = new Set(normalized.map((field) => field.id));

  return {
    id: createUniqueIdentifier(id || createGeneratedFieldId(), usedIds, "form-field"),
    fieldType: definition.value,
    name: createUniqueIdentifier(definition.name, usedNames, definition.name),
    type: definition.inputType,
    label:
      typeof label === "string" && label.trim() ? label : definition.fallbackLabel,
    placeholder: typeof placeholder === "string" ? placeholder : "",
    required: Boolean(required),
    ...(definition.hasOptions
      ? {
          options: normalizeFormFieldOptions([
            { label: "Option 1", value: "option_1" },
            { label: "Option 2", value: "option_2" },
          ]),
        }
      : {}),
  };
};

export const changeFormFieldType = ({
  field,
  fieldType,
  fields = [],
  label,
} = {}) => {
  const current = normalizeFormFields([field])[0] || createFormField();
  const definition = getFormFieldTypeDefinition(fieldType);
  const otherFields = normalizeFormFields(fields).filter(
    (candidate) => candidate.id !== current.id
  );
  const usedNames = new Set(otherFields.map((candidate) => candidate.name));

  return {
    ...current,
    fieldType: definition.value,
    name: createUniqueIdentifier(definition.name, usedNames, definition.name),
    type: definition.inputType,
    label:
      typeof label === "string" && label.trim()
        ? label
        : current.label || definition.fallbackLabel,
    placeholder: current.placeholder || "",
    options: definition.hasOptions
      ? current.options?.length
        ? normalizeFormFieldOptions(current.options)
        : normalizeFormFieldOptions([
            { label: "Option 1", value: "option_1" },
            { label: "Option 2", value: "option_2" },
          ])
      : [],
  };
};

/**
 * Resolve an allowlisted semantic field into safe React form-control props.
 */
export const resolveFormFieldRenderConfig = (field = {}) => {
  const fieldType = resolveFormFieldType(field);
  const definition = getFormFieldTypeDefinition(fieldType);
  const attributes = {};

  if (fieldType === "fullName") attributes.autoComplete = "name";
  if (fieldType === "firstName") attributes.autoComplete = "given-name";
  if (fieldType === "lastName") attributes.autoComplete = "family-name";
  if (fieldType === "email") attributes.autoComplete = "email";
  if (fieldType === "phone") {
    attributes.autoComplete = "tel";
    attributes.inputMode = "tel";
  }
  if (fieldType === "vehicleYear") {
    attributes.min = 1886;
    attributes.inputMode = "numeric";
  }
  if (fieldType === "mileage") {
    attributes.min = 0;
    attributes.inputMode = "numeric";
  }
  if (fieldType === "number") attributes.inputMode = "decimal";
  if (fieldType === "vin") {
    attributes.maxLength = 17;
    attributes.autoCapitalize = "characters";
    attributes.spellCheck = false;
  }
  if (field.minLength !== undefined) attributes.minLength = field.minLength;
  if (field.maxLength !== undefined) attributes.maxLength = field.maxLength;
  if (field.min !== undefined) attributes.min = field.min;
  if (field.max !== undefined) attributes.max = field.max;
  if (field.pattern) {
    try {
      // Validate before exposing it to the browser constraint engine.
      // eslint-disable-next-line no-new
      new RegExp(field.pattern);
      attributes.pattern = field.pattern;
    } catch (_error) {
      // Invalid legacy patterns remain editable but never reach public markup.
    }
  }

  const element = ["textarea", "select", "radio"].includes(
    definition.inputType
  )
    ? definition.inputType
    : "input";
  return {
    fieldType,
    element,
    inputType:
      ["textarea", "select", "radio"].includes(definition.inputType)
        ? undefined
        : definition.inputType,
    attributes,
    options: definition.hasOptions
      ? normalizeFormFieldOptions(field.options)
      : [],
  };
};

export const duplicateFormField = (field, fields = []) => {
  const normalized = normalizeFormFields(fields);
  const source = normalizeFormFields([field])[0];
  if (!source) return null;
  const usedIds = new Set(normalized.map((candidate) => candidate.id));
  const usedNames = new Set(normalized.map((candidate) => candidate.name));
  return {
    ...source,
    id: createUniqueIdentifier(createGeneratedFieldId(), usedIds, "form-field"),
    name: createUniqueIdentifier(`${source.name}_copy`, usedNames, source.name),
    label: `${source.label} copy`,
    ...(source.options
      ? {
          options: source.options.map((option, index) => ({
            ...option,
            id: `${createGeneratedFieldId()}-option-${index + 1}`,
          })),
        }
      : {}),
  };
};

const fieldValue = (values, field) =>
  values?.[field.id] !== undefined ? values[field.id] : values?.[field.name];

export const validateFormValues = (fields, values = {}) => {
  const normalizedFields = normalizeFormFields(fields);
  const errors = {};
  const normalizedValues = {};
  normalizedFields.forEach((field) => {
    const raw = fieldValue(values, field);
    const value = field.fieldType === "checkbox"
      ? Boolean(raw)
      : String(raw ?? "").trim();
    normalizedValues[field.id] = value;
    const requiredMissing = field.fieldType === "checkbox"
      ? field.required && value !== true
      : field.required && !value;
    if (requiredMissing) {
      errors[field.id] =
        field.requiredMessage || `${field.label || field.name} is required`;
      return;
    }
    if (value === "" || value === false) return;
    if (
      ["select", "radio"].includes(field.fieldType) &&
      !field.options.some((option) => option.value === value)
    ) {
      errors[field.id] = field.validationMessage || "Select a valid option";
      return;
    }
    if (field.fieldType === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors[field.id] = field.validationMessage || "Enter a valid email address";
      return;
    }
    const numeric = Number(value);
    if (
      ["number", "vehicleYear", "mileage"].includes(field.fieldType) &&
      !Number.isFinite(numeric)
    ) {
      errors[field.id] = field.validationMessage || "Enter a valid number";
      return;
    }
    if (field.minLength !== undefined && value.length < field.minLength) {
      errors[field.id] =
        field.validationMessage || `Enter at least ${field.minLength} characters`;
      return;
    }
    if (field.maxLength !== undefined && value.length > field.maxLength) {
      errors[field.id] =
        field.validationMessage || `Enter no more than ${field.maxLength} characters`;
      return;
    }
    if (field.min !== undefined && Number.isFinite(numeric) && numeric < field.min) {
      errors[field.id] = field.validationMessage || `Value must be at least ${field.min}`;
      return;
    }
    if (field.max !== undefined && Number.isFinite(numeric) && numeric > field.max) {
      errors[field.id] = field.validationMessage || `Value must be at most ${field.max}`;
      return;
    }
    if (field.pattern) {
      try {
        if (!new RegExp(field.pattern).test(value)) {
          errors[field.id] = field.validationMessage || "Enter a valid value";
        }
      } catch (_error) {
        // Invalid legacy pattern is ignored consistently with public attributes.
      }
    }
  });
  return {
    valid: Object.keys(errors).length === 0,
    errors,
    values: normalizedValues,
  };
};

export const resolveSafeFormRedirect = (value) => {
  const redirect = String(value || "").trim();
  if (!redirect) return "";
  if (redirect.startsWith("/") && !redirect.startsWith("//")) return redirect;
  try {
    const parsed = new URL(redirect);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.toString() : "";
  } catch (_error) {
    return "";
  }
};

export const repairCaptchaFormRelationships = (forms, captchas) => {
  const formList = Array.isArray(forms) ? forms : [];
  const diagnostics = [];
  const nextCaptchas = (Array.isArray(captchas) ? captchas : []).map((captcha) => {
    const pageId = captcha.pageId || "home";
    const pageForms = formList.filter((form) => (form.pageId || "home") === pageId);
    const validTarget = pageForms.some((form) => form.id === captcha.targetFormId);
    if (validTarget) return captcha;
    const targetFormId = pageForms[0]?.id || "";
    diagnostics.push({
      captchaId: captcha.id,
      previousTargetFormId: captcha.targetFormId || "",
      targetFormId,
      repaired: Boolean(targetFormId),
    });
    return { ...captcha, targetFormId };
  });
  return { captchas: nextCaptchas, diagnostics };
};
